import { ComboBox, ComboBoxOption } from "@shared/designSystem/ComboBox";
import { VerticalContainer } from "@shared/designSystem/Containers";
import { QSwitch } from "@shared/designSystem/QSwitch";
import { TextInput } from "@shared/designSystem/TextInput";
import { SmallText } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import type { CollaboratorCustomField } from "@services/CollaboratorCustomFieldsService";
import { sanitizeCustomFieldInput } from "@utils/CustomFieldValues";

interface CustomFieldInputProps {
    field: CollaboratorCustomField;
    /** Form value (dates as DD/MM/AAAA) */
    value: string;
    error?: string;
    disabled?: boolean;
    onChange: (value: string) => void;
    onEnterPress?: () => void;
}

/**
 * Input for one custom field value, by type: text, number, date (DD/MM/AAAA),
 * yes/no switch or list combo. Same look and rules as the collaborator profile.
 */
export const CustomFieldInput: React.FC<CustomFieldInputProps> = ({
    field,
    value,
    error,
    disabled = false,
    onChange,
    onEnterPress,
}) => {
    const label = field.isRequired ? `${field.name} *` : field.name;
    const handleChange = (nextValue: string) => onChange(sanitizeCustomFieldInput(field, nextValue));

    if (field.typeId === "boolean") {
        const checked = value === "true";
        return (
            <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
                <SmallText text={label} />
                <div
                    style={{
                        width: "100%",
                        minHeight: "50px",
                        border: `1px solid ${error ? designSystemStyles.colorError : designSystemStyles.colorTextInputBorder}`,
                        borderRadius: designSystemStyles.cornerRadiusComponent,
                        boxShadow: designSystemStyles.shadowTextInput,
                        backgroundColor: designSystemStyles.colorBackgroundWhite,
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0 12px",
                        gap: "12px",
                    }}
                >
                    <span
                        style={{
                            color: designSystemStyles.colorText,
                            fontFamily: designSystemStyles.fontFamily,
                            fontSize: designSystemStyles.fontSizeSm,
                            fontWeight: designSystemStyles.fontWeightText,
                        }}
                    >
                        {checked ? "Sí".localized() : "No".localized()}
                    </span>
                    <QSwitch
                        checked={checked}
                        disabled={disabled}
                        onChange={() => onChange(checked ? "false" : "true")}
                    />
                </div>
                {error ? <SmallText text={error} color={designSystemStyles.colorError} /> : null}
            </VerticalContainer>
        );
    }

    if (field.typeId === "list") {
        const options: ComboBoxOption[] = (field.values || []).map((listValue) => ({
            id: listValue.id,
            title: listValue.label,
        }));
        const selectedOption = options.find((option) => option.id === value);
        return (
            <VerticalContainer width="100%" gap={designSystemStyles.gapXxxSm}>
                <SmallText text={label} />
                <ComboBox
                    options={field.isRequired ? options : [{ id: "", title: "Sin seleccionar".localized() }, ...options]}
                    value={selectedOption}
                    placeholder={"Seleccionar".localized()}
                    iconType={null as never}
                    disabled={disabled}
                    onChange={(option) => onChange(option.id)}
                />
                {error ? <SmallText text={error} color={designSystemStyles.colorError} /> : null}
            </VerticalContainer>
        );
    }

    return (
        <TextInput
            label={label}
            value={value}
            placeholder={field.typeId === "date" ? "DD/MM/AAAA".localized() : field.name}
            type={field.typeId === "number" ? "number" : undefined}
            inputMode={field.typeId === "number" ? "decimal" : field.typeId === "date" ? "numeric" : undefined}
            error={error}
            disabled={disabled}
            onChange={handleChange}
            onEnterPress={onEnterPress}
        />
    );
};

export default CustomFieldInput;
